/**
 * CalibrationWorkspace - everything about one strength-maturity calibration
 * (ASTM C1074-19 section 8), laid out like the "Mix Design / Recipe Summary"
 * mockup. Shown on the mix design page (embedded) and on /calibrations/:id.
 *
 *   top left      Strength vs Maturity Details (name, type, equation, pour,
 *                 sensor cylinders, concrete start)
 *   top right     temperature and maturity of the sensor cylinders (System),
 *                 or the ASTM checklist (Manual)
 *   bottom left   test results grid
 *   bottom right  calibration regression (the strength-maturity curve)
 *   right drawer  "More details": batch record, which sensors count,
 *                 checklist, history
 *
 * calId null = create a new calibration for the mix design.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import { MoreHoriz as MoreIcon } from '@mui/icons-material';
import calibrationsService, { Calibration, CalibrationSample } from '../../services/calibrations';
import productsService, { Product } from '../../services/products';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/userPermissions';
import { useCompanyStrengthUnit } from '../../utils/useCompanyStrengthUnit';
import { apiErrorText } from '../../utils/apiError';
import { MATURITY_UNIT_UNKNOWN } from '../../utils/maturityDisplay';
import CalibrationGraphTab from '../CalibrationGraphTab';
import CalibrationDetailsPanel from './CalibrationDetailsPanel';
import CalibrationResultsGrid from './CalibrationResultsGrid';
import TemperatureMaturityChart from './TemperatureMaturityChart';
import CalibrationChecklist from './CalibrationChecklist';
import CalibrationMoreDrawer from './CalibrationMoreDrawer';
import RemoveCalibrationDialog from './RemoveCalibrationDialog';
import { describeBasis, isSystemType } from './calibrationFormat';

const Panel: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>{title}</Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

export interface CalibrationWorkspaceProps {
  /** null = create mode. */
  calId: string | null;
  productId?: string;
  /** Embedded on the mix design page: no page title (the host shows the picker). */
  embedded?: boolean;
  /** A calibration was created, or a new version made: show this one. */
  onOpen: (calId: string) => void;
  /** Anything changed that the host's list should reflect (status, active, results). */
  onChanged?: () => void;
  /** Left of the header actions, e.g. the host's calibration picker. */
  headerLeft?: React.ReactNode;
  /** The calibration was deleted. */
  onDeleted?: () => void;
}

const CalibrationWorkspace: React.FC<CalibrationWorkspaceProps> = ({
  calId, productId = '', embedded, onOpen, onChanged, headerLeft, onDeleted,
}) => {
  const { user } = useAuth();
  // Mirrors the backend gates (utils/rbac.py).
  const canCreate = hasPermission(user, 'mix_designs.create_maturity_curve');
  const canEdit = hasPermission(user, 'mix_designs.edit_maturity_curve');
  const canActivate = hasPermission(user, 'mix_designs.link_activate');

  const [calibration, setCalibration] = useState<Calibration | null>(null);
  const [samples, setSamples] = useState<CalibrationSample[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [activeOnProduct, setActiveOnProduct] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(!!calId);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [confirm, setConfirm] = useState<'finish' | 'activate' | 'new-version' | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  // Bumped after every change so the charts reload.
  const [version, setVersion] = useState(0);

  // The calibration's own company (a superadmin may view another company's).
  // Blank until it is known: never the signed-in user's company as a guess.
  const strengthUnit = useCompanyStrengthUnit(calibration?.company_id || product?.company_id) || '';

  const load = useCallback(async () => {
    if (!calId) return;
    try {
      const [cal, rows] = await Promise.all([
        calibrationsService.get(calId),
        calibrationsService.listSamples(calId),
      ]);
      setCalibration(cal);
      setSamples(rows);
      setVersion((v) => v + 1);
      onChanged?.();
      if (productId) {
        const links = await productsService.listProductCalibrations(productId).catch(() => null);
        setActiveOnProduct(links ? !!links.find((l) => l.calibration_id === calId)?.is_active : null);
      }
    } catch (e) {
      setError(apiErrorText(e, 'The calibration could not be loaded'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calId, productId]);

  useEffect(() => {
    setLoading(!!calId);
    setCalibration(null);
    load();
  }, [load, calId]);

  useEffect(() => {
    if (!productId) return;
    productsService.getProduct(productId).then(setProduct).catch(() => undefined);
  }, [productId]);

  const runAction = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    try {
      const message = await fn();
      if (message) setSuccess(message);
      setConfirm(null);
      setAcknowledged(false);
    } catch (e) {
      setError(apiErrorText(e, 'The action failed'));
    } finally {
      setBusy(false);
    }
  };

  if (calId && loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (calId && !calibration) {
    return <Alert severity="error">{error || 'Calibration not found'}</Alert>;
  }
  if (!calId && !canCreate) {
    return <Alert severity="warning">You do not have permission to create a calibration.</Alert>;
  }

  const cal = calibration;
  const locked = !!cal?.is_locked;
  const readOnly = !canEdit || locked;
  const system = isSystemType(cal?.calibration_type ?? 's');
  const checks = cal?.checks;
  const maturityUnit = cal?.basis?.unit || MATURITY_UNIT_UNKNOWN;
  const mixCompare = checks?.mix_comparison;

  return (
    <Box>
      {/* Header: name, one status, one main action, everything else in the menu */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        {headerLeft}
        {!embedded && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" component="h1" noWrap>{cal ? cal.name || cal.code : 'New calibration'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {product ? `Mix ${product.code}` : ''}{cal ? `${product ? ' · ' : ''}${cal.code}` : ''}
            </Typography>
          </Box>
        )}
        {cal && (
          <Chip size="small" sx={{ ml: 1 }}
            label={activeOnProduct ? 'Active' : locked ? 'Finished' : 'In progress'}
            color={activeOnProduct ? 'success' : locked ? 'default' : 'warning'} />
        )}
        <Box sx={{ flexGrow: 1 }} />
        {cal && !locked && canEdit && (
          <Button variant="contained" size="small" onClick={() => setConfirm('finish')}>Finish</Button>
        )}
        {cal && locked && productId && !activeOnProduct && canActivate && (
          <Button variant="contained" size="small" onClick={() => setConfirm('activate')}>Make active</Button>
        )}
        {cal && (
          <>
            <IconButton size="small" aria-label="more actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreIcon />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => { setMenuAnchor(null); setDrawerOpen(true); }}>More details</MenuItem>
              {locked && canCreate && (
                <MenuItem onClick={() => { setMenuAnchor(null); setConfirm('new-version'); }}>New version</MenuItem>
              )}
              {canEdit && (
                <MenuItem sx={{ color: 'error.main' }} onClick={() => {
                  setMenuAnchor(null);
                  setRemoveOpen(true);
                }}>Delete</MenuItem>
              )}
            </Menu>
          </>
        )}
      </Box>

      {mixCompare?.status === 'mismatch' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This calibration uses {describeBasis(checks?.basis)}, but the mix design uses {describeBasis(checks?.mix_basis)}.
          It cannot be made active on this mix (ASTM C1074 9.2).
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Panel title="Strength vs maturity details">
            <CalibrationDetailsPanel
              calibration={cal}
              companyId={product?.company_id || user?.CompanyID}
              productId={productId}
              productEquationId={product?.maturity_method_id}
              canEdit={cal ? canEdit : canCreate}
              hasResults={samples.length > 0}
              onCreated={(created) => {
                setSuccess('Calibration created');
                onChanged?.();
                onOpen(created.id);
              }}
              onSaved={() => { setSuccess('Saved'); load(); }}
              onError={setError}
            />
          </Panel>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          {cal && system ? (
            <Panel title="Temperature and maturity">
              <TemperatureMaturityChart calId={cal.id} samples={samples} reloadKey={version} />
            </Panel>
          ) : cal ? (
            <Panel title="Calibration regression">
              <CalibrationGraphTab key={version} compact calId={cal.id} readOnly={readOnly} canRecalculate={canEdit}
                maturityMethod={cal.basis?.method} specifiedTemp={cal.basis?.specified_temp} strengthUnit={strengthUnit} />
            </Panel>
          ) : (
            <Panel title="Temperature and maturity">
              <Typography variant="body2" color="text.secondary">
                Shows here once the calibration is created and its loggers are chosen.
              </Typography>
            </Panel>
          )}
        </Grid>
        {cal && (
          <>
            <Grid size={{ xs: 12, lg: system ? 7 : 12 }}>
              <Panel title="Test results">
                <CalibrationResultsGrid
                  calId={cal.id}
                  samples={samples}
                  isSystem={system}
                  specimens={cal.specimens || []}
                  readOnly={readOnly}
                  strengthUnit={strengthUnit}
                  maturityUnit={maturityUnit}
                  thirdCylinderAvailable={cal.supports_third_cylinder !== false}
                  onChanged={load}
                  onError={setError}
                />
              </Panel>
            </Grid>
            {system && (
              <Grid size={{ xs: 12, lg: 5 }}>
                <Panel title="Calibration regression">
                  <CalibrationGraphTab key={version} compact calId={cal.id} readOnly={readOnly} canRecalculate={canEdit}
                    maturityMethod={cal.basis?.method} specifiedTemp={cal.basis?.specified_temp} strengthUnit={strengthUnit} />
                </Panel>
              </Grid>
            )}
          </>
        )}
      </Grid>

      {cal && (
        <CalibrationMoreDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          calibration={cal}
          readOnly={readOnly}
          onSaved={() => { setSuccess('Saved'); load(); }}
          onError={setError}
        />
      )}

      {/* Finish / activate / new version */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {confirm === 'finish' ? 'Finish calibration' : confirm === 'activate' ? 'Make active on the mix' : 'Create a new version'}
        </DialogTitle>
        <DialogContent>
          {confirm === 'finish' && cal && (
            <>
              <Typography sx={{ mb: 2 }}>
                This fits the strength-maturity curve to the test results, records the maturity equation and locks the
                calibration. To change it later, make a new version.
              </Typography>
              {checks && <CalibrationChecklist checks={checks.checks} dense />}
              {checks?.has_fail && (
                <Alert severity="error" sx={{ mt: 2 }}>Fix the items marked with a cross before finishing.</Alert>
              )}
              {!checks?.has_fail && checks?.has_warn && (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
                  label="I have reviewed the warnings and want to finish anyway"
                />
              )}
            </>
          )}
          {confirm === 'activate' && cal && (
            <>
              <Typography>
                Pours of this mix design then estimate strength from this curve. The calibration that is active now becomes
                inactive. Running pours keep the calibration they started with.
              </Typography>
              {mixCompare?.status === 'differs' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  The datum or Q differs: calibration {describeBasis(checks?.basis)}, mix design {describeBasis(checks?.mix_basis)}.
                </Alert>
              )}
            </>
          )}
          {confirm === 'new-version' && (
            <Typography>
              This makes a draft copy with all test results. The current version stays finished and active, so existing
              pours are not affected. The new version applies only after you finish it and make it active.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || (confirm === 'finish' && (!!checks?.has_fail || (!!checks?.has_warn && !acknowledged)))
              || (confirm === 'activate' && mixCompare?.status === 'mismatch')}
            color={confirm === 'finish' ? 'success' : confirm === 'activate' ? 'warning' : 'primary'}
            onClick={() => {
              if (!cal) return;
              if (confirm === 'finish') {
                runAction(async () => { const r = await calibrationsService.finish(cal.id, true); await load(); return r.message; });
              } else if (confirm === 'activate') {
                runAction(async () => { const r = await calibrationsService.activate(cal.id, productId); await load(); return r.message; });
              } else {
                runAction(async () => {
                  const draft = await calibrationsService.newVersion(cal.id);
                  onChanged?.();
                  onOpen(draft.id);
                  return 'Draft version created. Edit the test results, then finish it and make it active.';
                });
              }
            }}
          >
            {busy ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <RemoveCalibrationDialog
        calibrationId={removeOpen && cal ? cal.id : null}
        productId={productId || undefined}
        canDelete={canEdit}
        canUnlink={false}
        onClose={() => setRemoveOpen(false)}
        onDeleted={() => {
          setRemoveOpen(false);
          onChanged?.();
          onDeleted?.();
        }}
      />

      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={8000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CalibrationWorkspace;
